import {
  Chrono,
  Close,
  NavigateRight,
} from '@randstad-lean-mobile-factory/react-assets/dist/icons';
import classnames from 'classnames';
import { useRef, useState } from 'react';

import { Button } from '../../Button/Button';
import { Menu, MenuActions, MenuItem } from '../../Menus';
import menuItemStyles from '../../Menus/MenuItem/MenuItem.module.scss';
import { usePositionEffect } from '../../utils';
import { TextInput } from '../TextInput';

import styles from './TimePickerInput.module.scss';
import { TimePickerInputProps } from './TimePickerInput.types';

export const TimePickerInput = <TClearable extends boolean = true>({
  size,
  arrow,
  placeholder,
  value,
  onChange,
  clearable: _clearable,
  className,
  error,
  disabled,
  showMinutes = true,
  showSeconds,
}: TimePickerInputProps<TClearable>) => {
  const clearable = _clearable !== false;

  const [hours, minutes, seconds] = value
    ?.split(':')
    .map(t => parseInt(t.startsWith('0') ? t.slice(1) : t)) ?? [undefined, undefined, undefined];

  const [isOpen, setIsOpen] = useState(false);

  const [inputValue, setInputValue] = useState('');

  useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<MenuActions>(null);

  const [bottom, setBottom] = useState(false);
  const [height, setHeight] = useState(0);

  const triggerRef = usePositionEffect(() => {
    if (isOpen) return;
    const boundingBox = triggerRef.current?.getBoundingClientRect();
    const bottom = window.innerHeight - (boundingBox?.bottom ?? 0) > (boundingBox?.top ?? 0);
    const height =
      (bottom ? window.innerHeight - (boundingBox?.bottom ?? 0) : (boundingBox?.top ?? 0)) - 16;
    setHeight(height);
    setBottom(bottom);
  }, [isOpen]);

  return (
    <Menu
      trigger={
        <div
          tabIndex={disabled ? undefined : 0}
          className={classnames(styles.trigger, className, styles[size], {
            [styles.disabled]: disabled,
            [styles.error]: error,
          })}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
              if (disabled) return;
              setIsOpen(true);
              menuRef.current?.open();
            }
          }}
          ref={triggerRef}
        >
          <Chrono color="blue" />
          <div className={styles.triggerContent}>
            {value ? (
              showSeconds ? (
                value
              ) : (
                value.slice(0, 5)
              )
            ) : (
              <span className={styles.placeholder}>{placeholder}</span>
            )}
          </div>
          {arrow && <NavigateRight color="blue" />}
        </div>
      }
      onOpen={() => {
        setIsOpen(true);
        setInputValue(
          value
            ? showMinutes && showSeconds
              ? value
              : showMinutes
                ? value.slice(0, 5)
                : value.slice(0, 2) + ':00'
            : ''
        );
        setTimeout(() => {
          inputRef.current?.focus();
          inputRef.current?.parentElement?.parentElement
            ?.querySelectorAll('.' + menuItemStyles.focus)
            .forEach(item => item.scrollIntoView({ behavior: 'auto', block: 'center' }));
        });
      }}
      onClose={() => {
        setIsOpen(false);
      }}
      ref={menuRef}
      anchorOrigin={{ horizontal: 'left', vertical: bottom ? 'bottom' : 'top' }}
      transformOrigin={{ horizontal: 'left', vertical: bottom ? 'top' : 'bottom' }}
      disabled={disabled}
      className={styles.menu}
      // @ts-expect-error ---
      style={{ '--menu-height': height + 'px' }}
    >
      <TextInput
        value={inputValue}
        onValueChange={value => setInputValue(value)}
        autoFocus
        size="medium"
        placeholder={showMinutes && showSeconds ? 'hh:mm:ss' : 'hh:mm'}
        leftIcon={<Chrono />}
        rightIcon={
          value && clearable ? (
            <Button
              variant="tertiary"
              onClick={() => {
                setInputValue('');
                // @ts-expect-error ---
                onChange?.(undefined);
              }}
            >
              <Close />
            </Button>
          ) : null
        }
        onInput={event => {
          const nativeEvent = event.nativeEvent;
          if (!((e): e is InputEvent => e.type === 'input')(nativeEvent)) return;
          if (showMinutes && showSeconds) {
            let value = event.currentTarget.value
              .replace(/[^\d:]/g, '')
              .replace(/^[:]*/g, '')
              .replace(/:+/g, ':')
              .replace(/^(\d\d)(\d+)$/g, '$1:$2')
              .replace(/^(\d\d):(\d\d)(\d+)$/g, '$1:$2:$3');
            if (nativeEvent.inputType === 'insertText')
              value = value
                .replace(/^(\d\d)$/g, '$1:')
                .replace(/^(\d\d:\d\d)$/g, '$1:')
                .replace(/^(\d):$/g, '0$1:')
                .replace(/^(\d\d):(\d):$/g, '$1:0$2');
            event.currentTarget.value = value;
          } else {
            let value = event.currentTarget.value
              .replace(/[^\d:]/g, '')
              .replace(/^[:]*/g, '')
              .replace(/:+/g, ':')
              .replace(/^(\d\d)(\d+)$/g, '$1:$2');
            if (nativeEvent.inputType === 'insertText')
              value = value.replace(/^(\d\d)$/g, '$1:').replace(/^(\d):$/g, '0$1:');
            event.currentTarget.value = value;
          }
        }}
        onChange={event => {
          const value = event.currentTarget.value;
          setInputValue(value);
          let time: string | undefined =
            showMinutes && showSeconds
              ? value.match(/^\d\d:\d\d:\d\d$/)
                ? value
                : undefined
              : showMinutes
                ? value.match(/^\d\d:\d\d$/)
                  ? value + ':00'
                  : undefined
                : value.match(/^\d\d(:\d\d)?$/)
                  ? value.slice(0, 2) + ':00:00'
                  : undefined;
          const parsedTime = time
            ?.split(':')
            .map(t => parseInt(t.startsWith('0') ? t.slice(1) : t)) ?? [0, 0, 0];
          if (parsedTime[0] >= 24 || parsedTime[1] >= 60 || parsedTime[2] >= 60) time = undefined;
          // @ts-expect-error ---
          if (time || clearable) onChange?.(time);
        }}
        onBlur={() =>
          setInputValue(value ? (showMinutes && showSeconds ? value : value.slice(0, 5)) : '')
        }
        onKeyDown={event => {
          if (event.key === 'Enter') {
            setInputValue('');
            menuRef.current?.close();
          }
        }}
        containerClassName={styles.input}
        ref={inputRef}
      />
      <div className={styles.columns}>
        <div className={styles.column}>
          {new Array(24).fill(null).map((_, index) => (
            <MenuItem
              key={index}
              keepOpenOnClick
              onClick={() => {
                onChange?.(
                  [index, showMinutes ? minutes : 0, seconds]
                    .map(t => (t ?? 0).toString().padStart(2, '0'))
                    .join(':')
                );
                setInputValue(
                  [index, showMinutes ? minutes : 0, seconds]
                    .map(t => (t ?? 0).toString().padStart(2, '0'))
                    .join(':')
                    .slice(0, showSeconds ? undefined : 5)
                );
              }}
              selected={hours === index}
            >
              {index.toString().padStart(2, '0')}
              {!showMinutes && ':00'}
            </MenuItem>
          ))}
        </div>
        {showMinutes && (
          <div className={styles.column}>
            {new Array(60).fill(null).map((_, index) => (
              <MenuItem
                key={index}
                keepOpenOnClick
                onClick={() => {
                  onChange?.(
                    [hours, index, seconds].map(t => (t ?? 0).toString().padStart(2, '0')).join(':')
                  );
                  setInputValue(
                    [hours, index, seconds]
                      .map(t => (t ?? 0).toString().padStart(2, '0'))
                      .join(':')
                      .slice(0, showSeconds ? undefined : 5)
                  );
                }}
                selected={minutes === index}
              >
                {index.toString().padStart(2, '0')}
              </MenuItem>
            ))}
          </div>
        )}
        {showMinutes && showSeconds && (
          <div className={styles.column}>
            {new Array(60).fill(null).map((_, index) => (
              <MenuItem
                key={index}
                keepOpenOnClick
                onClick={() => {
                  onChange?.(
                    [hours, minutes, index].map(t => (t ?? 0).toString().padStart(2, '0')).join(':')
                  );
                  setInputValue(
                    [hours, index, seconds].map(t => (t ?? 0).toString().padStart(2, '0')).join(':')
                  );
                }}
                selected={seconds === index}
              >
                {index.toString().padStart(2, '0')}
              </MenuItem>
            ))}
          </div>
        )}
      </div>
    </Menu>
  );
};
